import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { auth, db, isAdminUser, orgIdFromEmail } from '../firebase';
import { logout as authLogout } from '../services/authService';
import { setUserRole } from '../../features/roleSetup/data/services/userRoleService';

// Every signed-up user gets this role (and its default responsibilities)
// assigned automatically at account creation — see ensureUserDocs.
const DEFAULT_ROLE = 'Australian Army';

// Exported for tests that provide a mock context value directly.
export const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

/**
 * Create the users/{uid} profile and organisations/{domain} docs on first
 * verified sign-in. Idempotent; rules only allow writing your own subtree
 * and your own domain's org doc.
 */
async function ensureUserDocs(fbUser) {
  const orgId = orgIdFromEmail(fbUser.email);
  const userRef = doc(db, 'users', fbUser.uid);
  const orgRef = doc(db, 'organisations', orgId);

  const [userSnap, orgSnap] = await Promise.all([getDoc(userRef), getDoc(orgRef)]);

  if (!orgSnap.exists()) {
    await setDoc(orgRef, {
      orgId,
      domain: orgId,
      name: orgId,
      createdAt: new Date().toISOString(),
    });
  }

  if (!userSnap.exists()) {
    await setDoc(userRef, {
      userId: fbUser.uid,
      email: fbUser.email.toLowerCase(),
      name: fbUser.displayName || fbUser.email.split('@')[0],
      orgId,
      onboardingCompleted: false,
      hasWeeklyPopup: false,
      createdAt: new Date().toISOString(),
    });

    // Auto-assign the default role and its responsibilities right away, so
    // this doesn't depend on the user completing either onboarding flow.
    try {
      await setUserRole(DEFAULT_ROLE, false);
    } catch (err) {
      console.warn('Default role auto-assign failed (role may not exist in DB):', err);
    }
  }
}

/** Shape the context `user` object the rest of the app expects. */
function buildUser(fbUser, profile) {
  return {
    ...profile,
    userId: fbUser.uid,
    id: fbUser.uid,
    email: fbUser.email,
    // Admin is derived from the verified email domain; firestore.rules is the
    // enforcement — this only shapes the UI (e.g. ProtectedAdminRoute).
    role: isAdminUser(fbUser) ? 'admin' : 'user',
  };
}

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [weeklyPrompts, setWeeklyPrompts] = useState([]);

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } catch (err) {
      console.error('Sign-out error:', err);
    }
    // State reset happens in the auth listener, but clear eagerly too so the
    // UI responds immediately.
    setUser(null);
    setOrganization(null);
    setIsAuthenticated(false);
    setWeeklyPrompts([]);
  }, []);

  useEffect(() => {
    let profileUnsubscribe = null;

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (profileUnsubscribe) {
        profileUnsubscribe();
        profileUnsubscribe = null;
      }

      setFirebaseUser(fbUser);

      // No session, or signed in but email not yet verified: treat as logged
      // out. The unverified session is kept in Firebase Auth so the login
      // screen can offer "resend verification email".
      if (!fbUser || !fbUser.emailVerified) {
        setUser(null);
        setOrganization(null);
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      try {
        await ensureUserDocs(fbUser);

        const orgId = orgIdFromEmail(fbUser.email);
        const orgSnap = await getDoc(doc(db, 'organisations', orgId));
        const orgData = orgSnap.exists() ? orgSnap.data() : { orgId, domain: orgId };
        setOrganization({ ...orgData, orgId, id: orgId });

        // Live-subscribe to the profile so updates (e.g. onboarding
        // completion) propagate without a reload.
        profileUnsubscribe = onSnapshot(
          doc(db, 'users', fbUser.uid),
          (snap) => {
            const profile = snap.exists() ? snap.data() : {};
            setUser(buildUser(fbUser, profile));
            setIsAuthenticated(true);
            setLoading(false);
          },
          (err) => {
            console.error('Profile subscription error:', err);
            // Keep the session usable with the minimal Auth-derived profile.
            setUser(buildUser(fbUser, {}));
            setIsAuthenticated(true);
            setLoading(false);
          }
        );
      } catch (err) {
        console.error('Auth initialization error:', err);
        setUser(null);
        setOrganization(null);
        setIsAuthenticated(false);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (profileUnsubscribe) profileUnsubscribe();
    };
  }, []);

  const value = {
    user,
    organization,
    firebaseUser,
    loading,
    isAuthenticated,
    weeklyPrompts,
    setWeeklyPrompts,
    logout,
    setUser,
    setOrganization,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
